import { FeedItemToken, IFeedItemToken } from '@app/models/FeedItemToken'
import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  RawDraftContentBlock,
  RawDraftContentState,
  RawDraftEntity,
  RawDraftEntityRange,
} from 'draft-js'

const MARKUP_LINE_SEPARATOR = '<br/>'

const STAFF_ID = 'staff'

export const MENTION_PATTERN = '\\@\\[\\w\\|\\w+\\|[^\\]]+\\]'
export const TAG_PATTERN = '#[A-Za-z_][A-Za-z0-9_\\-]*'

export enum MentionType {
  Companies = 'companies',
  Connections = 'connections',
  DevelopmentSite = 'development-site',
  Properties = 'properties',
  Staff = 'staff',
  Team = 'team',
  Listing = 'listing',
}

export class Mention {
  public readonly id: string
  public readonly text: string
  public readonly type: string

  constructor(tokenText: string) {
    if (!tokenText) {
      return
    }

    const props = tokenText.substring(2, tokenText.length - 1).split('|')

    if (props.length > 2) {
      this.type = props[0]
      this.id = props[1]
      this.text = props[2]
    }
  }

  public getDetailUrl(): string {
    switch (this.getMentionType()) {
      case MentionType.Properties:
        return `/properties/property/${this.id}`

      case MentionType.DevelopmentSite:
        return `/development/sites/${this.id}`

      case MentionType.Team:
        return `/team/${this.id}`

      case MentionType.Staff:
        return ''

      case MentionType.Listing:
        return `/brokerage/listing/${this.id}`

      default:
        return `/connection/${this.id}`
    }
  }

  public getMentionType(): MentionType {
    switch (this.type) {
      case 'M':
        return MentionType.Companies

      case 'P':
        return MentionType.Properties

      case 'S':
        return this.id === STAFF_ID
          ? MentionType.Staff
          : MentionType.DevelopmentSite

      case 'T':
        return MentionType.Team

      case 'L':
        return MentionType.Listing

      default:
        return MentionType.Connections
    }
  }
}

export function convertToMarkup(content: ContentState): string {
  const rawContent = convertToRaw(content)

  if (rawContent.blocks.length === 0) {
    return ''
  }

  const output: string[] = rawContent.blocks.map<string>((block) => {
    if (block.entityRanges.length === 0) {
      return block.text
    } else {
      let blockText = block.text

      for (let i = block.entityRanges.length - 1; i >= 0; i--) {
        const entityRange = block.entityRanges[i]
        const entity = rawContent.entityMap[entityRange.key]
        const mention = entity.data.mention as IFeedItemToken

        if (mention) {
          blockText =
            blockText.substring(0, entityRange.offset) +
            `@[${mention.type}|${mention.id}|${mention.name}]` +
            blockText.substring(entityRange.offset + entityRange.length)
        }
      }

      return blockText
    }
  })

  return output.join(MARKUP_LINE_SEPARATOR)
}

export function convertFromMarkup(markup: string): ContentState {
  if (!markup) {
    return new ContentState()
  }

  const rawContentState: RawDraftContentState = {
    blocks: [],
    entityMap: {},
  }

  const rawBlocks = markup.split(MARKUP_LINE_SEPARATOR)

  rawContentState.blocks = rawBlocks.map((blockText, index) =>
    toRawDraftContentBlock(blockText, rawContentState.entityMap, index),
  )

  return convertFromRaw(rawContentState)
}

function toRawDraftContentBlock(
  text: string,
  entityMap: { [key: string]: RawDraftEntity },
  index: number,
): RawDraftContentBlock {
  const matcher = new RegExp(MENTION_PATTERN, 'g')
  let lText = text

  let match = matcher.exec(lText)
  const entityRanges: RawDraftEntityRange[] = []

  while (match != null) {
    const matchText = match.toString()
    const mention = new Mention(matchText)

    const entityKey = Object.keys(entityMap).length

    const entity: RawDraftEntity = {
      data: {
        mention: new FeedItemToken({
          id: mention.id,
          name: mention.text,
          type: mention.type,
        }),
      },
      mutability: 'IMMUTABLE',
      type: 'mention',
    }

    entityMap[entityKey] = entity

    entityRanges.push({
      key: entityKey,
      length: mention.text.length,
      offset: match.index,
    })

    lText =
      lText.substring(0, match.index) +
      mention.text +
      lText.substring(match.index + match[0].length)

    matcher.lastIndex = match.index
    match = matcher.exec(lText)
  }

  return {
    data: {},
    depth: 0,
    entityRanges,
    inlineStyleRanges: [],
    key: `mrk${index}`,
    text: lText,
    type: 'unstyled',
  }
}
